import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Plus, PencilSimple, Trash, MapTrifold, Stethoscope, UserCircle, CalendarCheck, X } from '@phosphor-icons/react';

export default function BeatManagementPage() {
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif', color: '#0A2540' }}>Beat Management</h1>
      <p className="text-sm" style={{ color: '#8A94A6' }}>Manage beats, assign doctors, and create monthly beat plans for MRs</p>
      <Tabs defaultValue="beats">
        <TabsList>
          <TabsTrigger value="beats">Beats</TabsTrigger>
          <TabsTrigger value="monthly">Monthly Beat Plans</TabsTrigger>
        </TabsList>
        <TabsContent value="beats"><BeatsTab /></TabsContent>
        <TabsContent value="monthly"><MonthlyBeatPlans /></TabsContent>
      </Tabs>
    </div>
  );
}

function BeatsTab() {
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editBeat, setEditBeat] = useState(null);
  const [showDoctorDialog, setShowDoctorDialog] = useState(null);

  const load = () => { api.getBeats().then(r => { setBeats(r.data); setLoading(false); }); };
  useEffect(() => { load(); }, []);

  const deleteBeat = async (id) => {
    if (window.confirm('Delete this beat?')) { await api.deleteBeat(id); load(); }
  };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-between items-center">
        <p className="text-sm font-medium" style={{ color: '#8A94A6' }}>{beats.length} beats configured</p>
        <Button onClick={() => { setEditBeat(null); setShowForm(true); }} className="h-9 px-4 text-sm font-semibold" style={{ background: '#0A2540', color: 'white' }}>
          <Plus size={16} className="mr-1" /> Add Beat
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? <p className="col-span-3 text-center py-8" style={{ color: '#8A94A6' }}>Loading...</p> :
          beats.map(beat => (
            <div key={beat._id} className="rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: '#007AFF10' }}>
                      <MapTrifold size={18} weight="duotone" style={{ color: '#007AFF' }} />
                    </div>
                    <div>
                      <p className="text-sm font-bold" style={{ color: '#0A2540' }}>{beat.name}</p>
                      <p className="text-[10px]" style={{ color: '#8A94A6' }}>{beat.area}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => { setEditBeat(beat); setShowForm(true); }} className="p-1 rounded hover:bg-slate-100"><PencilSimple size={14} style={{ color: '#007AFF' }} /></button>
                    <button onClick={() => deleteBeat(beat._id)} className="p-1 rounded hover:bg-red-50"><Trash size={14} style={{ color: '#FF3B30' }} /></button>
                  </div>
                </div>
                {beat.description && <p className="text-xs mb-3" style={{ color: '#8A94A6' }}>{beat.description}</p>}
              </div>

              {/* Doctors in beat */}
              <div className="px-4 pb-3">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-bold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Doctors ({(beat.doctors || []).length})</p>
                  <button onClick={() => setShowDoctorDialog(beat)} className="text-[10px] font-semibold" style={{ color: '#007AFF' }}>+ Add Doctor</button>
                </div>
                <div className="space-y-1.5">
                  {(beat.doctors || []).length === 0 ? (
                    <p className="text-xs py-2 text-center" style={{ color: '#8A94A6' }}>No doctors assigned</p>
                  ) : (beat.doctors || []).map((doc, di) => (
                    <div key={di} className="flex items-center gap-2 p-2 rounded" style={{ background: '#F5F6F8' }}>
                      <Stethoscope size={14} style={{ color: '#007AFF' }} />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium truncate" style={{ color: '#0A2540' }}>{doc.name}</p>
                        <p className="text-[10px] truncate" style={{ color: '#8A94A6' }}>{doc.specialization} | {doc.area}</p>
                      </div>
                      <button onClick={async () => { await api.removeDoctorFromBeat(beat._id, doc.id); load(); }} className="p-0.5 rounded hover:bg-red-50">
                        <X size={12} style={{ color: '#FF3B30' }} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))
        }
      </div>

      {/* Beat Form */}
      <Dialog open={showForm} onOpenChange={() => setShowForm(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editBeat ? 'Edit Beat' : 'Create Beat'}</DialogTitle></DialogHeader>
          <BeatForm beat={editBeat} onSave={() => { setShowForm(false); load(); }} />
        </DialogContent>
      </Dialog>

      {/* Add Doctor to Beat Dialog */}
      <AddDoctorToBeatDialog beat={showDoctorDialog} onClose={() => setShowDoctorDialog(null)} onAdded={() => { setShowDoctorDialog(null); load(); }} />
    </div>
  );
}

function BeatForm({ beat, onSave }) {
  const [form, setForm] = useState({ name: beat?.name || '', area: beat?.area || '', description: beat?.description || '' });
  const save = async () => {
    try {
      if (beat?._id) await api.updateBeat(beat._id, form);
      else await api.createBeat(form);
      onSave();
    } catch {}
  };
  return (
    <div className="space-y-3">
      <div><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Beat Name</Label>
        <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="h-8 mt-1" placeholder="e.g. Beat 1 - Sector 15" /></div>
      <div><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Area</Label>
        <Input value={form.area} onChange={e => setForm(f => ({ ...f, area: e.target.value }))} className="h-8 mt-1" /></div>
      <div><Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Description</Label>
        <Input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="h-8 mt-1" /></div>
      <Button onClick={save} className="w-full h-9 text-sm font-semibold" style={{ background: '#0A2540', color: 'white' }}>Save Beat</Button>
    </div>
  );
}

function AddDoctorToBeatDialog({ beat, onClose, onAdded }) {
  const [doctors, setDoctors] = useState([]);
  const [search, setSearch] = useState('');
  useEffect(() => { if (beat) api.getDoctors(search).then(r => setDoctors(r.data)); }, [beat, search]);

  if (!beat) return null;
  const existingIds = (beat.doctors || []).map(d => d.id);

  const add = async (docId) => {
    await api.addDoctorToBeat(beat._id, docId);
    onAdded();
  };

  return (
    <Dialog open={!!beat} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle>Add Doctor to {beat.name}</DialogTitle></DialogHeader>
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search doctors..." className="h-8" />
        <div className="max-h-60 overflow-y-auto space-y-1 mt-2">
          {doctors.filter(d => !existingIds.includes(d.id)).map(d => (
            <button key={d.id} onClick={() => add(d.id)} className="w-full flex items-center gap-2 p-2 rounded text-left hover:bg-slate-50" style={{ border: '1px solid #E2E4E9' }}>
              <Stethoscope size={14} style={{ color: '#007AFF' }} />
              <div className="flex-1"><p className="text-xs font-medium">{d.name}</p><p className="text-[10px]" style={{ color: '#8A94A6' }}>{d.specialization} | {d.area}</p></div>
              <Plus size={14} style={{ color: '#34C759' }} />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MonthlyBeatPlans() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [plans, setPlans] = useState([]);
  const [users, setUsers] = useState([]);
  const [beats, setBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssign, setShowAssign] = useState(false);

  const load = () => {
    Promise.all([
      api.getMonthlyBeatPlans(month),
      api.getUsers('mr'),
      api.getBeats()
    ]).then(([pRes, uRes, bRes]) => {
      setPlans(pRes.data);
      setUsers(uRes.data);
      setBeats(bRes.data);
      setLoading(false);
    });
  };
  useEffect(() => { load(); }, [month]);

  return (
    <div className="space-y-4 mt-4">
      <div className="flex items-end justify-between">
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Month</Label>
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="h-8 mt-1 w-48" />
        </div>
        <Button onClick={() => setShowAssign(true)} className="h-9 px-4 text-sm font-semibold" style={{ background: '#0A2540', color: 'white' }}>
          <CalendarCheck size={16} className="mr-1" /> Assign Beat Plan
        </Button>
      </div>

      {/* Plan List */}
      <div className="space-y-3">
        {loading ? <p className="text-center py-8" style={{ color: '#8A94A6' }}>Loading...</p> :
          plans.length === 0 ? (
            <div className="p-8 text-center rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
              <CalendarCheck size={40} weight="duotone" style={{ color: '#E2E4E9' }} className="mx-auto mb-2" />
              <p className="text-sm" style={{ color: '#8A94A6' }}>No beat plans assigned for {month}</p>
            </div>
          ) : plans.map((plan, i) => (
            <div key={i} className="p-4 rounded-md border" style={{ background: '#FFFFFF', borderColor: '#E2E4E9' }}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold text-white" style={{ background: '#007AFF' }}>
                  {plan.user_name?.charAt(0) || 'M'}
                </div>
                <div>
                  <p className="text-sm font-bold" style={{ color: '#0A2540' }}>{plan.user_name}</p>
                  <p className="text-xs" style={{ color: '#8A94A6' }}>Month: {plan.month} | {(plan.beats || []).length} beats assigned</p>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                {(plan.beats || []).map((beat, bi) => (
                  <div key={bi} className="p-2.5 rounded" style={{ background: '#F5F6F8' }}>
                    <p className="text-xs font-bold" style={{ color: '#0A2540' }}>{beat.name}</p>
                    <p className="text-[10px]" style={{ color: '#8A94A6' }}>{beat.area} | {(beat.doctors || []).length} doctors</p>
                    <div className="mt-1 space-y-0.5">
                      {(beat.doctors || []).map((doc, di) => (
                        <p key={di} className="text-[10px] flex items-center gap-1" style={{ color: '#8A94A6' }}>
                          <Stethoscope size={10} /> {doc.name}
                        </p>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              {plan.notes && <p className="text-xs mt-2" style={{ color: '#8A94A6' }}>Notes: {plan.notes}</p>}
            </div>
          ))
        }
      </div>

      {/* Assign Dialog */}
      <Dialog open={showAssign} onOpenChange={() => setShowAssign(false)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Assign Monthly Beat Plan</DialogTitle></DialogHeader>
          <AssignBeatForm month={month} users={users} beats={beats} onSave={() => { setShowAssign(false); load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AssignBeatForm({ month, users, beats, onSave }) {
  const [form, setForm] = useState({ user_id: '', user_name: '', month, beat_ids: [], notes: '' });
  const [selectedBeats, setSelectedBeats] = useState([]);

  const toggleBeat = (beatId) => {
    setSelectedBeats(prev => prev.includes(beatId) ? prev.filter(b => b !== beatId) : [...prev, beatId]);
  };

  const save = async () => {
    if (!form.user_id || selectedBeats.length === 0) return;
    try {
      await api.assignMonthlyBeatPlan({ ...form, beat_ids: selectedBeats });
      onSave();
    } catch {}
  };

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Select MR</Label>
        <Select value={form.user_id} onValueChange={v => { const u = users.find(x => x._id === v); setForm(f => ({ ...f, user_id: v, user_name: u?.name || '' })); }}>
          <SelectTrigger className="h-9 mt-1"><SelectValue placeholder="Choose MR" /></SelectTrigger>
          <SelectContent>{users.map(u => <SelectItem key={u._id} value={u._id}>{u.name} ({u.phone})</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Month</Label>
        <Input type="month" value={form.month} onChange={e => setForm(f => ({ ...f, month: e.target.value }))} className="h-9 mt-1" />
      </div>
      <div>
        <Label className="text-[10px] font-semibold uppercase tracking-widest mb-2 block" style={{ color: '#8A94A6' }}>Select Beats ({selectedBeats.length} selected)</Label>
        <div className="grid grid-cols-1 gap-1.5 max-h-48 overflow-y-auto">
          {beats.map(beat => {
            const selected = selectedBeats.includes(beat._id);
            return (
              <button key={beat._id} onClick={() => toggleBeat(beat._id)}
                className="flex items-center gap-2 p-2.5 rounded text-left transition-colors"
                style={{ background: selected ? '#007AFF10' : '#F5F6F8', border: `1px solid ${selected ? '#007AFF40' : '#E2E4E9'}` }}>
                <div className="w-5 h-5 rounded flex items-center justify-center text-xs font-bold"
                  style={{ background: selected ? '#007AFF' : '#E2E4E9', color: selected ? 'white' : '#8A94A6' }}>
                  {selected ? '✓' : ''}
                </div>
                <div className="flex-1">
                  <p className="text-xs font-bold" style={{ color: '#0A2540' }}>{beat.name}</p>
                  <p className="text-[10px]" style={{ color: '#8A94A6' }}>{beat.area} | {(beat.doctors || []).length} doctors</p>
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <Label className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: '#8A94A6' }}>Notes</Label>
        <Input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="h-9 mt-1" placeholder="Optional instructions" />
      </div>
      <Button onClick={save} disabled={!form.user_id || selectedBeats.length === 0}
        className="w-full h-9 text-sm font-semibold" style={{ background: '#0A2540', color: 'white' }}>
        Assign {selectedBeats.length} Beat(s) to MR
      </Button>
    </div>
  );
}
